import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import { Maximize2, X } from "lucide-react";
import L from "leaflet";

// A top-layer dialog avoids clipped/transformed card and assignment-modal ancestors.
// Moving the existing container preserves the Leaflet instance and its current view.
const useExpandedMap = (map, expanded, close, testId, trigger) => {
  useLayoutEffect(() => {
    if (!expanded) return;
    const node = map.getContainer();
    const triggerNode = trigger.current;
    const anchor = document.createComment("map-position");
    const dialog = document.createElement("dialog");
    dialog.className = "map-fullscreen-dialog";
    dialog.dataset.testid = `${testId}-fullscreen-dialog`;
    dialog.setAttribute("aria-label", "Full-screen map");
    const overflow = document.body.style.overflow;
    const modes = ["dragging", "scrollWheelZoom", "doubleClickZoom", "touchZoom", "keyboard"].map((key) => [map[key], map[key].enabled()]);
    const resize = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      map.invalidateSize({ pan: false });
      map.setView(center, zoom, { animate: false });
    };
    const escape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault(); event.stopImmediatePropagation(); close();
      }
    };
    const cancel = (event) => { event.preventDefault(); event.stopPropagation(); close(); };
    map.stop();
    node.before(anchor);
    node.parentNode.appendChild(dialog);
    dialog.appendChild(node);
    node.classList.add("map-expanded");
    document.body.style.overflow = "hidden";
    modes.forEach(([handler]) => handler.enable());
    window.addEventListener("keydown", escape, true);
    dialog.addEventListener("cancel", cancel);
    dialog.showModal();
    resize();
    triggerNode?.focus({ preventScroll: true });
    const observer = new ResizeObserver(resize);
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.removeEventListener("keydown", escape, true);
      dialog.removeEventListener("cancel", cancel);
      node.classList.remove("map-expanded");
      anchor.replaceWith(node);
      dialog.close(); dialog.remove();
      document.body.style.overflow = overflow;
      modes.forEach(([handler, enabled]) => { if (!enabled) handler.disable(); });
      if (node.isConnected) resize();
      triggerNode?.focus({ preventScroll: true });
    };
  }, [map, expanded, close, testId, trigger]);
};

export const MapFullscreenControl = ({ testId }) => {
  const map = useMap();
  const trigger = useRef(null);
  const [container, setContainer] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const close = useCallback(() => setExpanded(false), []);
  useExpandedMap(map, expanded, close, testId, trigger);
  useEffect(() => {
    const control = L.control({ position: "topright" });
    control.onAdd = () => {
      const node = L.DomUtil.create("div", "leaflet-bar leaflet-control-fullscreen");
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
      setContainer(node);
      return node;
    };
    control.addTo(map);
    return () => control.remove();
  }, [map]);
  return container && createPortal(<button ref={trigger} type="button" data-testid={`${testId}-fullscreen`} aria-label={expanded ? "Exit full-screen map" : "Expand map to full screen"} aria-expanded={expanded} title={expanded ? "Exit full-screen map" : "Full-screen map"} onClick={() => setExpanded((value) => !value)}>
    {expanded ? <><X size={18} aria-hidden="true" /><span>Close Map</span></> : <Maximize2 size={18} aria-hidden="true" />}
  </button>, container);
};
